'use client';

import {
  type PropsWithChildren,
  createContext,
  useContext,
  useMemo,
} from 'react';

import classnames from 'classnames';

import type {
  DateStreakContextValue,
  DateStreakDayProps,
  DateStreakProps,
} from './DateStreak.types';
import {
  DAY_LABELS,
  getWeekStart,
  isDateInArray,
  normalizeDate,
  normalizeDateArray,
} from './DateStreak.utils';
import { DateStreakCalendar } from './layout/DateStreakCalendar';
import { DateStreakProtectors } from './layout/DateStreakProtectors';
import { DateStreakWeek } from './layout/DateStreakWeek';

import styles from './DateStreak.module.scss';

const DateStreakContext = createContext<DateStreakContextValue | undefined>(
  undefined,
);

export const useDateStreakContext = () => {
  const context = useContext(DateStreakContext);
  if (context === undefined) {
    throw new Error(
      'useDateStreakContext must be used within a DateStreak component',
    );
  }
  return context;
};

const getDayStatus = (
  date: Date,
  highlightedDates: Date[],
  disabledDates: Date[],
  skippedDates: Date[],
): DateStreakDayProps['status'] => {
  // Automatically disable Sundays
  if (date.getDay() === 0) return 'disabled';
  if (isDateInArray(date, disabledDates)) return 'disabled';
  if (isDateInArray(date, skippedDates)) return 'skipped';
  if (isDateInArray(date, highlightedDates)) return 'highlighted';
  return 'empty';
};

function DateStreak({
  style,
  className,
  today,
  highlightedDates = [],
  disabledDates = [],
  skippedDates = [],
  locale = 'es',
  children,
  testId = 'ui-date-streak',
}: PropsWithChildren<DateStreakProps>) {
  const weekItems = useMemo<DateStreakDayProps[]>(() => {
    const validToday = normalizeDate(today);
    const weekStart = getWeekStart(validToday, locale);
    const normalizedHighlighted = normalizeDateArray(highlightedDates);
    const normalizedDisabled = normalizeDateArray(disabledDates);
    const normalizedSkipped = normalizeDateArray(skippedDates);
    const dayLabels = DAY_LABELS[locale] ?? DAY_LABELS.es;
    const normalizedToday = normalizeDate(validToday);

    return dayLabels.map((label, index) => {
      const dayDate = new Date(weekStart);
      dayDate.setDate(weekStart.getDate() + index);
      const normalizedDayDate = normalizeDate(dayDate);

      return {
        label,
        today: normalizedDayDate.getTime() === normalizedToday.getTime(),
        status: getDayStatus(
          normalizedDayDate,
          normalizedHighlighted,
          normalizedDisabled,
          normalizedSkipped,
        ),
      };
    });
  }, [today, highlightedDates, disabledDates, skippedDates, locale]);

  const contextValue = useMemo<DateStreakContextValue>(
    () => ({
      highlightedDates,
      disabledDates,
      skippedDates,
      locale,
    }),
    [highlightedDates, disabledDates, skippedDates, locale],
  );

  return (
    <DateStreakContext.Provider value={contextValue}>
      <div
        className={classnames(styles['date-streak'], className)}
        style={style}
        data-testid={testId}
      >
        <DateStreakWeek items={weekItems} />
        {children}
      </div>
    </DateStreakContext.Provider>
  );
}

const DateStreakWithComponents = DateStreak as typeof DateStreak & {
  Calendar: typeof DateStreakCalendar;
  Protectors: typeof DateStreakProtectors;
};

DateStreakWithComponents.Calendar = DateStreakCalendar;
DateStreakWithComponents.Protectors = DateStreakProtectors;

export { DateStreakWithComponents as DateStreak };
